/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  DeleteAdminV1OrgStoresIdPathParameters,
  GetAdminV1OrgStores200,
  GetAdminV1OrgStoresIdPathParameters,
  GetAdminV1OrgStoresIdReadinessPathParameters,
  PatchAdminV1OrgStoresIdBody,
  PatchAdminV1OrgStoresIdPathParameters,
  PostAdminV1OrgStoresBody,
  Store,
  StoreReadiness,
  TimeZoneList
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * A membership restricted to some stores lists only those. Bounded list, no pagination
 * @summary Stores of the organization the caller may act on
 */
export const getAdminV1OrgStores = (

 options?: SecondParameter<typeof customInstance<GetAdminV1OrgStores200>>,) => {
      return customInstance<GetAdminV1OrgStores200>(
      {url: `/admin/v1/org/stores`, method: 'GET'
    },
      options);
    }
  /**
 * Needs stores.write and a membership that covers every store. At most 20 stores per organization
 * @summary Create a store in draft status
 */
export const postAdminV1OrgStores = (
    postAdminV1OrgStoresBody: BodyType<PostAdminV1OrgStoresBody>,
 options?: SecondParameter<typeof customInstance<Store>>,) => {
      return customInstance<Store>(
      {url: `/admin/v1/org/stores`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1OrgStoresBody
    },
      options);
    }
  /**
 * @summary Get a store with its private settings
 */
export const getAdminV1OrgStoresId = (
    { id }: GetAdminV1OrgStoresIdPathParameters,
 options?: SecondParameter<typeof customInstance<Store>>,) => {
      return customInstance<Store>(
      {url: `/admin/v1/org/stores/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * Status follows draft → active, active ↔ paused, any → closed (final); other transitions are 409. draft → active answers 409 STORE_NOT_READY with meta.checks while a readiness blocker fails, unless `force` is true. Changing defaultCurrency answers 409 STORE_CURRENCY_LOCKED with meta.reasons once orders, price overrides, shipping rates or markets in another currency exist; otherwise the default market follows. nextOrderNumber only moves forward (409 below the current value). Settings groups are merged, not replaced. A closed store is read-only (409 STORE_READ_ONLY). settings.write covers the settings groups; anything else in the body (profile, status, domain) needs stores.write
 * @summary Update store profile, status or settings
 */
export const patchAdminV1OrgStoresId = (
    { id }: PatchAdminV1OrgStoresIdPathParameters,
    patchAdminV1OrgStoresIdBody: BodyType<PatchAdminV1OrgStoresIdBody>,
 options?: SecondParameter<typeof customInstance<Store>>,) => {
      return customInstance<Store>(
      {url: `/admin/v1/org/stores/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1OrgStoresIdBody
    },
      options);
    }
  /**
 * @summary Delete a draft store. Live stores are closed with PATCH instead
 */
export const deleteAdminV1OrgStoresId = (
    { id }: DeleteAdminV1OrgStoresIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/org/stores/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Computed from the store's data on every call: a published priced product, a payment method, shipping for the store country and a priced default market are blockers; tax, API key, domain and policies are warnings. A draft store with a failed blocker cannot become active unless the PATCH passes force
 * @summary Go-live checklist for the store
 */
export const getAdminV1OrgStoresIdReadiness = (
    { id }: GetAdminV1OrgStoresIdReadinessPathParameters,
 options?: SecondParameter<typeof customInstance<StoreReadiness>>,) => {
      return customInstance<StoreReadiness>(
      {url: `/admin/v1/org/stores/${id}/readiness`, method: 'GET'
    },
      options);
    }
  /**
 * Static list from the runtime's ICU data plus the modern names it also resolves. `timezone` on store create and update accepts any zone the runtime resolves, so links such as US/Eastern pass too
 * @summary IANA time zones a store may use
 */
export const getAdminV1MetaTimezones = (

 options?: SecondParameter<typeof customInstance<TimeZoneList>>,) => {
      return customInstance<TimeZoneList>(
      {url: `/admin/v1/meta/timezones`, method: 'GET'
    },
      options);
    }
  export type GetAdminV1OrgStoresResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgStores>>>
export type PostAdminV1OrgStoresResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgStores>>>
export type GetAdminV1OrgStoresIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgStoresId>>>
export type PatchAdminV1OrgStoresIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgStoresId>>>
export type DeleteAdminV1OrgStoresIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1OrgStoresId>>>
export type GetAdminV1OrgStoresIdReadinessResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgStoresIdReadiness>>>
export type GetAdminV1MetaTimezonesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1MetaTimezones>>>
